import React, { useEffect, useMemo, useState } from "react";
import {
  FiAlertCircle,
  FiCheckCircle,
  FiInfo,
  FiPlus,
  FiRefreshCw,
  FiSave,
  FiSettings,
  FiTrash2,
  FiX,
} from "react-icons/fi";
import { getApiUrl } from "../utils/api";
import "../styles/settingsModal.css";

const LabSectionConfigModal = ({ isModalVisible, setIsModalVisible }) => {
  const [sections, setSections] = useState([]);
  const [defaultCondition, setDefaultCondition] = useState("A");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const resetState = () => {
    setSections([]);
    setDefaultCondition("A");
    setError("");
    setSuccess("");
    setLoading(false);
    setSaving(false);
  };

  const closeModal = () => {
    resetState();
    setIsModalVisible(false);
  };

  const sanitizeCondition = (value) => {
    const upper = (value || "A").toString().trim().toUpperCase();
    return upper === "B" ? "B" : "A";
  };

  const loadConfig = async () => {
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const response = await fetch(getApiUrl("/api/experiment/labs"), {
        method: "GET",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });

      if (!response.ok) {
        throw new Error(`Failed to load lab sections (status ${response.status})`);
      }

      const data = await response.json();
      const loadedSections = Array.isArray(data.labSections)
        ? data.labSections.map((section) => ({
            id: (section.id || "").trim(),
            label: (section.label || section.id || "").trim(),
            condition: sanitizeCondition(section.condition),
          }))
        : [];

      setSections(loadedSections);
      setDefaultCondition(sanitizeCondition(data.defaultCondition));
    } catch (err) {
      setError(err.message || "Failed to load lab sections");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isModalVisible) {
      loadConfig();
    } else {
      resetState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalVisible]);

  const handleSectionChange = (index, field, value) => {
    setSections((prev) =>
      prev.map((section, idx) =>
        idx === index
          ? {
              ...section,
              [field]: field === "condition" ? sanitizeCondition(value) : value,
            }
          : section
      )
    );
  };

  const handleAddSection = () => {
    setSections((prev) => [
      ...prev,
      {
        id: "",
        label: "",
        condition: "A",
      },
    ]);
  };

  const handleRemoveSection = (index) => {
    setSections((prev) => prev.filter((_, idx) => idx !== index));
  };

  const validationMessage = useMemo(() => {
    if (sections.length === 0) {
      return "Add at least one lab section so students can select it.";
    }

    const seen = new Set();
    for (const section of sections) {
      const id = (section.id || "").trim();
      const label = (section.label || "").trim();
      if (!id || !label) {
        return "Every section needs both an identifier and a display label.";
      }
      const key = id.toLowerCase();
      if (seen.has(key)) {
        return "Section identifiers must be unique.";
      }
      seen.add(key);
    }

    return "";
  }, [sections]);

  const handleSave = async () => {
    setError("");
    setSuccess("");

    if (validationMessage) {
      setError(validationMessage);
      return;
    }

    setSaving(true);

    const payload = {
      labSections: sections.map((section) => ({
        id: (section.id || "").trim(),
        label: (section.label || "").trim(),
        condition: sanitizeCondition(section.condition),
      })),
      defaultCondition: sanitizeCondition(defaultCondition),
    };

    try {
      const response = await fetch(getApiUrl("/api/experiment/labs"), {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (!response.ok || data.status !== "success") {
        throw new Error(data.message || data.error || "Failed to save configuration");
      }

      setSections(
        Array.isArray(data.labSections)
          ? data.labSections.map((section) => ({
              id: (section.id || "").trim(),
              label: (section.label || section.id || "").trim(),
              condition: sanitizeCondition(section.condition),
            }))
          : []
      );
      setDefaultCondition(sanitizeCondition(data.defaultCondition));
      setSuccess("Lab sections updated. Students will see the new options immediately.");
    } catch (err) {
      setError(err.message || "Failed to save configuration");
    } finally {
      setSaving(false);
    }
  };

  if (!isModalVisible) {
    return null;
  }

  return (
    <div className="modal-overlay" onClick={closeModal}>
      <div
        className="modal-container"
        onClick={(event) => event.stopPropagation()}
        style={{
          maxWidth: "720px",
          width: "90%",
          maxHeight: "82vh",
          overflowY: "auto",
          padding: 0,
        }}
      >
        <div
          className="modal-header"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "20px 24px",
            borderBottom: "1px solid #e5e7eb",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <FiSettings size={24} color="#6366f1" />
            <h2 style={{ margin: 0, fontSize: "20px", fontWeight: 600 }}>
              Configure Lab Sections
            </h2>
          </div>
          <button
            onClick={closeModal}
            style={{
              background: "transparent",
              border: "none",
              cursor: "pointer",
              padding: "8px",
              borderRadius: "6px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <FiX size={20} />
          </button>
        </div>

        <div style={{ padding: "24px", fontSize: "14px", color: "#374151" }}>
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: "12px",
              backgroundColor: "#f1f5f9",
              borderRadius: "12px",
              padding: "16px",
              marginBottom: "20px",
            }}
          >
            <FiInfo size={18} color="#2563eb" style={{ minWidth: "18px" }} />
            <div>
              <strong>Define the lab sections your students will choose from.</strong>
              <p style={{ margin: "8px 0 0 0", color: "#475569" }}>
                Each section pairs an identifier with a human-friendly label and a condition (A or B).
                Students adopt the condition tied to the section they select.
              </p>
            </div>
          </div>

          {error && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                backgroundColor: "#fef2f2",
                border: "1px solid #fecaca",
                borderRadius: "10px",
                padding: "12px 16px",
                color: "#b91c1c",
                marginBottom: "16px",
              }}
            >
              <FiAlertCircle size={18} />
              <span>{error}</span>
            </div>
          )}

          {success && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                backgroundColor: "#f0fdf4",
                border: "1px solid #bbf7d0",
                borderRadius: "10px",
                padding: "12px 16px",
                color: "#166534",
                marginBottom: "16px",
              }}
            >
              <FiCheckCircle size={18} />
              <span>{success}</span>
            </div>
          )}

          {loading ? (
            <div style={{ textAlign: "center", padding: "40px", color: "#6b7280" }}>
              Loading lab sections…
            </div>
          ) : (
            <>
              <div style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "12px",
                gap: "16px",
              }}>
                <label style={{ fontWeight: 600 }}>Default condition</label>
                <select
                  value={defaultCondition}
                  onChange={(event) => setDefaultCondition(sanitizeCondition(event.target.value))}
                  style={{
                    padding: "8px 12px",
                    borderRadius: "8px",
                    border: "1px solid #d1d5db",
                    fontSize: "14px",
                  }}
                >
                  <option value="A">Condition A</option>
                  <option value="B">Condition B</option>
                </select>
              </div>

              <div
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: "12px",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 140px 64px",
                    gap: "12px",
                    padding: "12px 16px",
                    backgroundColor: "#f9fafb",
                    fontWeight: 600,
                  }}
                >
                  <span>Identifier</span>
                  <span>Display label</span>
                  <span>Condition</span>
                  <span></span>
                </div>

                {sections.length === 0 ? (
                  <div style={{ padding: "20px", textAlign: "center", color: "#6b7280" }}>
                    No sections yet. Add one to get started.
                  </div>
                ) : (
                  sections.map((section, index) => (
                    <div
                      key={`lab-section-${index}`}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr 140px 64px",
                        gap: "12px",
                        padding: "12px 16px",
                        borderTop: "1px solid #e5e7eb",
                        alignItems: "center",
                      }}
                    >
                      <input
                        type="text"
                        value={section.id}
                        onChange={(event) =>
                          handleSectionChange(index, "id", event.target.value)
                        }
                        placeholder="e.g., LAB-A"
                        style={{
                          padding: "8px 12px",
                          borderRadius: "8px",
                          border: "1px solid #d1d5db",
                          fontSize: "14px",
                        }}
                      />
                      <input
                        type="text"
                        value={section.label}
                        onChange={(event) =>
                          handleSectionChange(index, "label", event.target.value)
                        }
                        placeholder="e.g., Morning Lab"
                        style={{
                          padding: "8px 12px",
                          borderRadius: "8px",
                          border: "1px solid #d1d5db",
                          fontSize: "14px",
                        }}
                      />
                      <select
                        value={section.condition}
                        onChange={(event) =>
                          handleSectionChange(index, "condition", event.target.value)
                        }
                        style={{
                          padding: "8px 12px",
                          borderRadius: "8px",
                          border: "1px solid #d1d5db",
                          fontSize: "14px",
                        }}
                      >
                        <option value="A">Condition A</option>
                        <option value="B">Condition B</option>
                      </select>
                      <button
                        onClick={() => handleRemoveSection(index)}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          background: "transparent",
                          border: "none",
                          cursor: "pointer",
                          color: "#b91c1c",
                        }}
                        aria-label="Remove section"
                      >
                        <FiTrash2 size={18} />
                      </button>
                    </div>
                  ))
                )}
              </div>

              <button
                onClick={handleAddSection}
                style={{
                  marginTop: "16px",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "10px 16px",
                  borderRadius: "8px",
                  border: "1px solid #c4b5fd",
                  backgroundColor: "#f5f3ff",
                  color: "#5b21b6",
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                <FiPlus size={18} />
                Add section
              </button>
            </>
          )}

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "12px",
              marginTop: "24px",
            }}
          >
            <button
              onClick={closeModal}
              style={{
                padding: "10px 16px",
                borderRadius: "8px",
                border: "1px solid #d1d5db",
                backgroundColor: "white",
                cursor: "pointer",
                fontWeight: 600,
              }}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                padding: "10px 18px",
                borderRadius: "8px",
                border: "none",
                backgroundColor: saving ? "#c7d2fe" : "#4f46e5",
                color: "white",
                cursor: saving ? "not-allowed" : "pointer",
                fontWeight: 600,
                opacity: saving ? 0.7 : 1,
              }}
              disabled={saving}
            >
              <FiSave size={18} />
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LabSectionConfigModal;
